class GitHubService {
  private readonly baseUrl = 'https://api.github.com';
  private readonly owner = 'ngtanthanh-qc';
  private readonly repo = 'resume-website';
  private readonly resumePath = 'resume.md';
  private cache: { content: string; timestamp: number; etag?: string } | null = null;
  private fileCache = new Map<string, { content: string; timestamp: number; etag?: string }>();
  private readonly cacheTimeout = 5 * 60 * 1000; // 5 minutes
  private preloadQueue = new Set<string>();

  constructor() {
    // Initialize with empty cache
    this.cache = null;
  }

  async fetchResumeContent(): Promise<string> {
    console.log('🔄 Fetching resume content...');
    
    // Try GitHub API first, then fallback to local

    try {
      // Check if we have valid cached content
      if (this.cache && this.isCacheValid()) {
        return this.cache.content;
      }

      const headers: Record<string, string> = {
        'Accept': 'application/vnd.github.v3+json',
        'User-Agent': 'resume-website-chatbot'
      };

      // Add GitHub token if available for higher rate limits
      const token = import.meta.env?.VITE_GITHUB_TOKEN as string;
      if (token) {
        headers['Authorization'] = `token ${token}`;
        console.log('🔑 Using GitHub token for API requests');
      } else {
        console.log('⚠️ No GitHub token, using public API (rate limited)');
      }

      // Add ETag for conditional requests
      if (this.cache?.etag) {
        headers['If-None-Match'] = this.cache.etag;
      }

      const url = `${this.baseUrl}/repos/${this.owner}/${this.repo}/contents/${this.resumePath}`;
      console.log('📡 Fetching from GitHub API:', url);
      
      const response = await fetch(url, { headers });

      // If content hasn't changed, return cached version
      if (response.status === 304 && this.cache) {
        this.cache.timestamp = Date.now();
        return this.cache.content;
      }

      if (!response.ok) {
        throw new Error(`GitHub API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      // Decode base64 content from GitHub API
      if (!data.content) {
        throw new Error('No content field in GitHub API response');
      }
      
      // Decode base64 to UTF-8 properly
      const base64Content = data.content.replace(/\n/g, '');
      const content = this.decodeBase64UTF8(base64Content);
      console.log('📝 Decoded content preview:', content.substring(0, 200) + '...');
      
      // Update cache
      this.cache = {
        content,
        timestamp: Date.now(),
        etag: response.headers.get('ETag') || undefined
      };

      console.log('✅ Successfully loaded from GitHub API');
      return content;
    } catch (error) {
      console.error('❌ Error fetching resume from GitHub:', error);
      
      // Return cached content if available, even if expired
      if (this.cache) {
        console.warn('Using expired cached resume content');
        return this.cache.content;
      }
      
      // Try local fallback before static fallback
      try {
        console.log('📄 Falling back to local resumeData...');
        const { resumeData } = await import('../data/resumeData');
        if (resumeData) {
          const content = this.generateMarkdownFromResumeData(resumeData);
          console.log('✅ Successfully loaded from local resumeData fallback');
          return content;
        }
      } catch (localError) {
        console.warn('Failed to load local resumeData:', localError);
      }
      
      // Final fallback content if everything fails
      console.warn('Using static fallback content');
      return this.getFallbackContent();
    }
  }

  private isCacheValid(): boolean {
    if (!this.cache) return false;
    return Date.now() - this.cache.timestamp < this.cacheTimeout;
  }

  private getFallbackContent(): string {
    return `# Thanh Nguyen - Software Engineer in Test

I apologize, but I'm having trouble accessing the latest resume content from GitHub. 
Please visit the website directly to see the most up-to-date information about Thanh's experience and qualifications.

## Core Competencies
- Automation Testing
- Python Programming  
- API Testing
- CI/CD Pipeline Management
- Project Leadership

For the most current and detailed information, please refer to the live resume on the website.`;
  }

  async validateRepoAccess(): Promise<boolean> {
    try {
      const url = `${this.baseUrl}/repos/${this.owner}/${this.repo}`;
      const response = await fetch(url);
      return response.ok;
    } catch {
      return false;
    }
  }

  getCacheInfo() {
    return {
      hasCachedContent: !!this.cache,
      isValid: this.isCacheValid(),
      timestamp: this.cache?.timestamp,
      age: this.cache ? Date.now() - this.cache.timestamp : null
    };
  }

  clearCache() {
    this.cache = null;
  }

  async refreshCache(): Promise<string> {
    this.clearCache();
    return this.fetchResumeContent();
  }

  async fetchFileContent(filePath: string): Promise<{ success: boolean; content?: string; error?: string }> {
    try {
      console.log('📂 Fetching file content:', filePath);
      
      // Check file cache first
      const cached = this.fileCache.get(filePath);
      if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
        console.log('✅ Using cached file content for:', filePath);
        return { success: true, content: cached.content };
      }

      const headers: Record<string, string> = {
        'Accept': 'application/vnd.github.v3+json',
        'User-Agent': 'resume-website'
      };

      // Add GitHub token if available
      const token = import.meta.env?.VITE_GITHUB_TOKEN as string;
      if (token) {
        headers['Authorization'] = `token ${token}`;
      }

      // Add ETag for conditional requests
      if (cached?.etag) {
        headers['If-None-Match'] = cached.etag;
      }

      const url = `${this.baseUrl}/repos/${this.owner}/${this.repo}/contents/${filePath}`;
      const response = await fetch(url, { headers });

      // If content hasn't changed, return cached version
      if (response.status === 304 && cached) {
        cached.timestamp = Date.now();
        return { success: true, content: cached.content };
      }

      if (!response.ok) {
        if (response.status === 404) {
          return { success: false, error: 'File not found' };
        }
        throw new Error(`GitHub API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      // Decode base64 content
      if (!data.content) {
        throw new Error('No content field in response');
      }
      
      // Decode base64 to UTF-8 properly
      const base64Content = data.content.replace(/\n/g, '');
      const content = this.decodeBase64UTF8(base64Content);
      
      // Update file cache
      this.fileCache.set(filePath, {
        content,
        timestamp: Date.now(),
        etag: response.headers.get('ETag') || undefined
      });

      console.log('✅ Successfully fetched file from GitHub:', filePath);
      return { success: true, content };
    } catch (error) {
      console.error('❌ Error fetching file from GitHub:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to fetch file' 
      };
    }
  }

  async preloadFile(filePath: string): Promise<void> {
    // Avoid duplicate preloads
    if (this.preloadQueue.has(filePath)) {
      return;
    }
    
    this.preloadQueue.add(filePath);
    
    // Preload in background
    this.fetchFileContent(filePath)
      .then(() => {
        console.log('📦 Preloaded file:', filePath);
      })
      .catch(error => {
        console.warn('Failed to preload file:', filePath, error);
      })
      .finally(() => {
        this.preloadQueue.delete(filePath);
      });
  }

  clearFileCache(filePath?: string) {
    if (filePath) {
      this.fileCache.delete(filePath);
    } else {
      this.fileCache.clear();
    }
  }

  private decodeBase64UTF8(base64: string): string {
    try {
      // Method 1: Try using browser's built-in TextDecoder
      if (typeof TextDecoder !== 'undefined') {
        const binaryString = atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return new TextDecoder('utf-8').decode(bytes);
      }
      
      // Method 2: Fallback for older browsers or environments
      const binaryString = atob(base64);
      // This handles UTF-8 encoding manually
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      // Convert UTF-8 bytes to string
      let result = '';
      let i = 0;
      while (i < bytes.length) {
        const byte1 = bytes[i++];
        
        if (byte1 < 0x80) {
          // 1-byte character (ASCII)
          result += String.fromCharCode(byte1);
        } else if (byte1 < 0xE0) {
          // 2-byte character
          const byte2 = bytes[i++];
          result += String.fromCharCode(((byte1 & 0x1F) << 6) | (byte2 & 0x3F));
        } else if (byte1 < 0xF0) {
          // 3-byte character
          const byte2 = bytes[i++];
          const byte3 = bytes[i++];
          result += String.fromCharCode(((byte1 & 0x0F) << 12) | ((byte2 & 0x3F) << 6) | (byte3 & 0x3F));
        } else {
          // 4-byte character (emoji, etc.)
          const byte2 = bytes[i++];
          const byte3 = bytes[i++];
          const byte4 = bytes[i++];
          const codePoint = ((byte1 & 0x07) << 18) | ((byte2 & 0x3F) << 12) | ((byte3 & 0x3F) << 6) | (byte4 & 0x3F);
          
          // Convert to surrogate pair for JavaScript string
          if (codePoint > 0xFFFF) {
            const highSurrogate = Math.floor((codePoint - 0x10000) / 0x400) + 0xD800;
            const lowSurrogate = ((codePoint - 0x10000) % 0x400) + 0xDC00;
            result += String.fromCharCode(highSurrogate, lowSurrogate);
          } else {
            result += String.fromCharCode(codePoint);
          }
        }
      }
      
      return result;
    } catch (error) {
      console.error('Error decoding base64 UTF-8:', error);
      // Fallback to basic atob if UTF-8 decoding fails
      return atob(base64);
    }
  }

  private generateMarkdownFromResumeData(resumeData: any): string {
    // Convert resumeData to markdown format
    let markdown = `# ${resumeData.personal?.name || 'Thanh Nguyen'}\n\n`;
    
    if (resumeData.personal?.title) {
      markdown += `**${resumeData.personal.title}**\n\n`;
    }
    
    if (resumeData.personal?.summary) {
      markdown += `## Summary\n${resumeData.personal.summary}\n\n`;
    }
    
    if (resumeData.experience?.length > 0) {
      markdown += `## Experience\n`;
      resumeData.experience.forEach((exp: any) => {
        markdown += `### ${exp.title} at ${exp.company}\n`;
        markdown += `*${exp.period}*\n\n`;
        if (exp.achievements) {
          exp.achievements.forEach((achievement: string) => {
            markdown += `- ${achievement}\n`;
          });
        }
        markdown += '\n';
      });
    }
    
    if (resumeData.skills?.length > 0) {
      markdown += `## Skills\n`;
      resumeData.skills.forEach((skill: any) => {
        if (typeof skill === 'string') {
          markdown += `- ${skill}\n`;
        } else if (skill.name) {
          markdown += `- ${skill.name}${skill.level ? ` (${skill.level})` : ''}\n`;
        }
      });
      markdown += '\n';
    }
    
    if (resumeData.education?.length > 0) {
      markdown += `## Education\n`;
      resumeData.education.forEach((edu: any) => {
        markdown += `### ${edu.degree || edu.title}\n`;
        markdown += `*${edu.school || edu.institution}* - ${edu.year || edu.period}\n\n`;
      });
    }
    
    if (resumeData.certifications?.length > 0) {
      markdown += `## Certifications\n`;
      resumeData.certifications.forEach((cert: any) => {
        markdown += `- ${cert.name || cert.title}`;
        if (cert.issuer) markdown += ` - ${cert.issuer}`;
        if (cert.year) markdown += ` (${cert.year})`;
        markdown += '\n';
      });
    }
    
    return markdown;
  }
}

export const githubService = new GitHubService();